import React, { useState, useEffect, useMemo } from "react";
import Sidebar, { SIDEBAR_WIDTH } from "./Sidebar";
import { Outlet } from "react-router-dom";
import { supabase } from './supabaseClient';

export default function StudentDashboardLayout() {
  const [sidebarVisible, setSidebarVisible] = useState(() => {
    const saved = localStorage.getItem('sidebarVisible');
    console.log('StudentDashboardLayout: Loading sidebar state from localStorage:', saved);
    const result = saved !== null ? JSON.parse(saved) : true;
    console.log('StudentDashboardLayout: Initial sidebar state:', result);
    return result;
  });
  // Initialize language from localStorage to stay consistent
  const [isArabic, setIsArabic] = useState(() => {
    const savedLang = localStorage.getItem('taealam_language');
    return savedLang === 'ar';
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // OPTIMIZATION: Cache user profile at layout level to persist across route changes
  const [cachedUserProfile, setCachedUserProfile] = useState(null);
  const [userProfileLoading, setUserProfileLoading] = useState(true);

  // Define fetchUserProfile at component level so it can be called from useEffect and refreshUserProfile
  const fetchUserProfile = React.useCallback(async () => {
    try {
      console.time('[Layout Performance] fetchUserProfile');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setUserProfileLoading(false);
        return;
      }

      // Fetch complete student profile
      const { data: student, error } = await supabase
        .from('students')
        .select('*')
        .eq('uuid_id', user.id)
        .single();

      if (!error && student) {
        setCachedUserProfile(student);
        console.log('[Layout] User profile cached:', student.full_name);
      }

      console.timeEnd('[Layout Performance] fetchUserProfile');
    } catch (error) {
      console.error('[Layout] Error fetching user profile:', error);
    } finally {
      setUserProfileLoading(false);
    }
  }, []); // Empty deps since we want a stable reference

  // Fetch user profile once and cache it
  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]); // Run once on mount
  
  // Handle window resize for mobile detection
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Persist sidebar state when it changes
  React.useEffect(() => {
    console.log('StudentDashboardLayout: Saving sidebar state to localStorage:', sidebarVisible);
    localStorage.setItem('sidebarVisible', JSON.stringify(sidebarVisible));
  }, [sidebarVisible]);

  // Handler for language change from Sidebar
  const handleLanguageChange = (arabic) => {
    console.log('StudentDashboardLayout: handleLanguageChange called with:', arabic);
    console.log('StudentDashboardLayout: Current isArabic before change:', isArabic);
    setIsArabic(arabic);
    // Save to localStorage to persist across pages
    localStorage.setItem('taealam_language', arabic ? 'ar' : 'en');
    // Update document direction
    document.documentElement.dir = arabic ? 'rtl' : 'ltr';
    document.documentElement.lang = arabic ? 'ar' : 'en';
    console.log('StudentDashboardLayout: isArabic state updated to:', arabic);
  };

  return (
    <div
      className={isArabic ? 'arabic-font' : ''}
      style={{ background: "#f7f9fb", minHeight: "100vh", width: "100%", position: 'relative' }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {/* Hamburger button to toggle sidebar */}
      {/* On mobile: hide close button, show smaller hamburger button without white background */}
      {(!isMobile || !sidebarVisible) && (
        <button
          aria-label={sidebarVisible ? (isArabic ? "إخفاء القائمة" : "Hide sidebar") : (isArabic ? "إظهار القائمة" : "Show sidebar")}
          onClick={() => setSidebarVisible((v) => !v)}
          style={{
            position: 'fixed',
            top: isMobile ? 12 : 18,
            left: isArabic ? 'auto' : (isMobile ? 12 : 18),
            right: isArabic ? (isMobile ? 12 : 18) : 'auto',
            zIndex: 2100,
            background: isMobile ? 'transparent' : '#fff',
            border: 'none',
            borderRadius: isMobile ? 0 : 8,
            boxShadow: isMobile ? 'none' : '0 2px 8px #eee',
            width: isMobile ? 28 : 44,
            height: isMobile ? 28 : 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {sidebarVisible ? (
            // Close (X) icon - hidden on mobile
            <svg width={isMobile ? 20 : 28} height={isMobile ? 20 : 28} viewBox="0 0 24 24" fill="none" stroke="#232B3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          ) : (
            // Hamburger icon - smaller on mobile, no background
            <svg width={isMobile ? 18 : 28} height={isMobile ? 18 : 28} viewBox="0 0 24 24" fill="none" stroke="#232B3E" strokeWidth={isMobile ? "2.5" : "2"} strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
          )}
        </button>
      )}
      {sidebarVisible && (
        <Sidebar
          userRole="student"
          isVisible={sidebarVisible}
          isOpen={true}
          setSidebarVisible={setSidebarVisible}
          isArabic={isArabic}
          onLanguageChange={handleLanguageChange}
          cachedUserProfile={cachedUserProfile}
        />
      )}
      <main
        className="p-4 xs:p-6 sm:p-8 md:p-10 lg:p-12
                   transition-all duration-300"
        style={{
          flex: 1,
          marginLeft: !isArabic && sidebarVisible ? (window.innerWidth > 900 ? SIDEBAR_WIDTH : 0) : 0,
          marginRight: isArabic && sidebarVisible ? (window.innerWidth > 900 ? SIDEBAR_WIDTH : 0) : 0,
        }}
      >
        <Outlet context={{
          sidebarVisible,
          setSidebarVisible,
          isArabic,
          handleLanguageChange,
          // OPTIMIZATION: Share cached user profile across all child routes
          cachedUserProfile,
          userProfileLoading,
          // Function to update cached profile if needed
          refreshUserProfile: () => {
            setUserProfileLoading(true);
            fetchUserProfile();
          }
        }} />
      </main>
    </div>
  );
} 